import { lazy, type ComponentType } from 'react';

/**
 * lazyWithReload — wraps React.lazy() so a stale-bundle chunk-load failure
 * (a tab left open across a deploy: the old bundle asks for
 * `/assets/PageX-<oldhash>.js`, which the new build replaced, so it 404s)
 * triggers ONE full `window.location.reload()` instead of falling into
 * Sentry.ErrorBoundary's generic "Something went wrong" and staying broken
 * until the user manually reloads.
 *
 * Session survives a reload on this app (verified), so a reload is safe —
 * the user lands back where they were with a fresh bundle instead of a dead
 * page.
 *
 * Guarded by a sessionStorage timestamp so it can never loop: if a reload
 * already happened in the last RELOAD_WINDOW_MS, the error is rethrown and
 * the ErrorBoundary shows as before (covers the pathological case of a
 * genuinely broken deploy, not just a stale tab).
 */
const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? err.name : '';
  return CHUNK_ERROR_PATTERN.test(message) || name === 'ChunkLoadError';
}

/** Reloads at most once per RELOAD_WINDOW_MS; returns true if it reloaded. */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode, quota) — fall through and reload
    // anyway; worst case is a loop this session, which a normal reload
    // still resolves once the bundle is current.
  }
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for React.lazy(): same signature, same Suspense
 * contract. On import failure it checks isChunkLoadError — if so, reloads
 * once via reloadOnceForChunkError() and returns a promise that never
 * resolves (the reload is about to tear the page down anyway); otherwise,
 * or if a reload already happened this window, it rethrows so Suspense's
 * error boundary handles it exactly as before.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (isChunkLoadError(err) && reloadOnceForChunkError()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
